/* ============================================================================
   PHONE AUTH SCREEN (Option B) — first-logon Firebase phone sign-in.
   number → invisible reCAPTCHA → SMS code → Firebase ID token → BFF exchange
   → real Yperea (QA) session. Firebase SDK is loaded lazily (dynamic import).
   ============================================================================ */
import React from 'react';
import type { ConfirmationResult, RecaptchaVerifier } from '../firebase/auth';
import { exchangeFirebase, type SessionInfo } from './api';
import { CONFIG, demoToggleAllowed, enterDemoMode } from '../config';

function friendlyError(e: unknown): string {
  const code = (e as { code?: string })?.code || '';
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'That number doesn’t look right — use international format, e.g. +44 7700 900001.';
    case 'auth/missing-phone-number':
      return 'Enter your phone number first.';
    case 'auth/too-many-requests':
      return 'Too many attempts — wait a moment and try again.';
    case 'auth/invalid-verification-code':
      return 'That code didn’t match. Check it and try again.';
    case 'auth/code-expired':
      return 'The code expired — request a new one.';
    case 'auth/captcha-check-failed':
      return 'Verification check failed — reload and try again.';
    case 'auth/invalid-app-credential':
    case 'auth/firebase-app-check-token-is-invalid':
      // NB: this is a reCAPTCHA / App-Check rejection, NOT a problem with the
      // phone number. Test numbers bypass reCAPTCHA, which is why they work.
      return 'reCAPTCHA/App Check rejected this origin. Add this exact domain to Firebase → Authentication → Settings → Authorized domains, and check App Check enforcement (see documentation/plan/07 §5.4/§5.7).';
    default:
      return (e as Error)?.message || 'Something went wrong. Please try again.';
  }
}

export function PhoneAuthScreen(props: { onAuthed: (s: SessionInfo) => void }): React.ReactElement {
  const [step, setStep] = React.useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = React.useState('+44');
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const verifierRef = React.useRef<RecaptchaVerifier | null>(null);
  const confirmationRef = React.useRef<ConfirmationResult | null>(null);

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      const fb = await import('../firebase/auth');
      // A reCAPTCHA token is single-use, so rebuild the verifier for every send
      // (reusing it after a successful render causes auth/invalid-app-credential).
      if (verifierRef.current) {
        try {
          verifierRef.current.clear();
        } catch {
          /* ignore */
        }
        verifierRef.current = null;
      }
      verifierRef.current = fb.makeInvisibleRecaptcha('yp-recaptcha');
      confirmationRef.current = await fb.startPhoneSignIn(phone.trim(), verifierRef.current);
      setStep('code');
    } catch (e) {
      setError(friendlyError(e));
      // a failed verifier can't be reused — drop it so the next attempt rebuilds it
      try {
        verifierRef.current?.clear();
      } catch {
        /* ignore */
      }
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode() {
    if (!confirmationRef.current) return;
    setError(null);
    setBusy(true);
    try {
      const fb = await import('../firebase/auth');
      const idToken = await fb.confirmCodeGetIdToken(confirmationRef.current, code.trim());
      const session = await exchangeFirebase(idToken); // → real QA session, stored by the BFF
      props.onAuthed(session);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const label = { color: 'var(--sub)', fontSize: 11.5 } as React.CSSProperties;
  return (
    <div
      className="yp-root"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 400,
          maxWidth: '100%',
          background: 'var(--panel-2)',
          border: '1px solid var(--line)',
          borderRadius: 26,
          backdropFilter: 'blur(30px)',
          padding: 30,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: 'var(--shadow)',
        }}
      >
        <span className="yp-h" style={{ fontSize: 22, color: 'var(--ink)' }}>
          Verify it’s you
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--sub)', marginTop: -8 }}>
          {step === 'phone'
            ? 'Connect your Yperea account to view live data. We’ll text you a one-time code.'
            : `Enter the 6-digit code we sent to ${phone}.`}
        </span>

        {step === 'phone' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={label}>Mobile number (international format)</span>
            <input
              className="yp-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+44 7700 900001"
              inputMode="tel"
              autoFocus
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={label}>Verification code</span>
            <input
              className="yp-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              autoFocus
              style={{ letterSpacing: '0.3em', fontFamily: "'Roboto Mono',monospace" }}
            />
          </div>
        )}

        {error ? (
          <span style={{ fontSize: 11.5, color: 'var(--red)', lineHeight: 1.5 }}>{error}</span>
        ) : null}

        {step === 'phone' ? (
          <button
            className="yp-btn"
            onClick={sendCode}
            disabled={busy}
            style={{
              width: '100%',
              background: 'var(--accent)',
              color: 'var(--on-accent)',
              border: 'none',
              borderRadius: 12,
              padding: '11px 16px',
              fontFamily: "'Quicksand',sans-serif",
              fontWeight: 700,
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              className="yp-btn"
              onClick={verifyCode}
              disabled={busy}
              style={{
                width: '100%',
                background: 'var(--accent)',
                color: 'var(--on-accent)',
                border: 'none',
                borderRadius: 12,
                padding: '11px 16px',
                fontFamily: "'Quicksand',sans-serif",
                fontWeight: 700,
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button
              className="yp-btn"
              onClick={() => {
                setStep('phone');
                setCode('');
                setError(null);
              }}
              style={{
                width: '100%',
                background: 'transparent',
                color: 'var(--sub)',
                border: '1px solid var(--line)',
                borderRadius: 12,
                padding: '9px 16px',
              }}
            >
              ← Use a different number
            </button>
          </div>
        )}

        <span style={{ fontSize: 10.5, color: 'var(--faint)', textAlign: 'center', lineHeight: 1.5 }}>
          Authenticating against the Yperea {CONFIG.env.toUpperCase()} environment.
        </span>
        {/* Staging only: skip live auth and review all role views with mock personas. */}
        {demoToggleAllowed ? (
          <span
            className="yp-btn"
            onClick={enterDemoMode}
            style={{ fontSize: 11, color: 'var(--accent)', textAlign: 'center', cursor: 'pointer' }}
          >
            Preview with demo personas →
          </span>
        ) : null}
        {/* invisible reCAPTCHA mount point (required by Firebase phone auth) */}
        <div id="yp-recaptcha" />
      </div>
    </div>
  );
}
